import React, { JSX, useRef, useState } from "react";

type JSONDebugPrintProps = {
    value: unknown;
};

const DEBUG_JSON_COLORS = {
    bg: "#1e1e1e",
    text: "#d4d4d4",
    key: "#9cdcfe",
    string: "#ce9178",
    number: "#b5cea8",
    boolean: "#569cd6",
    null: "#569cd6",
};

function formatNodePath(path: string): string {
    return path.length > 0 ? path : "$";
}

function formatNodeValue(value: unknown): string {
    if (typeof value === "string") {
        return value;
    }
    return JSON.stringify(value, null, 2);
}

function nodeMatchesFilter(value: unknown, filterText: string, path: string): boolean {
    const normalized = filterText.trim().toLowerCase();
    if (!normalized) {
        return true;
    }

    if (path.toLowerCase().includes(normalized)) {
        return true;
    }

    if (value === null || typeof value !== "object") {
        return String(value).toLowerCase().includes(normalized);
    }

    if (Array.isArray(value)) {
        return value.some((item, index) => nodeMatchesFilter(item, normalized, `${path}[${index}]`));
    }

    return Object.entries(value as Record<string, unknown>).some(([key, nested]) => {
        const childPath = path ? `${path}.${key}` : key;
        return key.toLowerCase().includes(normalized) || nodeMatchesFilter(nested, normalized, childPath);
    });
}

function renderJsonPrimitive(value: unknown): JSX.Element {
    if (value === null) {
        return <span style={{ color: DEBUG_JSON_COLORS.null }}>null</span>;
    }

    switch (typeof value) {
        case "string":
            return <span style={{ color: DEBUG_JSON_COLORS.string }}>&quot;{value}&quot;</span>;
        case "number":
            return <span style={{ color: DEBUG_JSON_COLORS.number }}>{String(value)}</span>;
        case "boolean":
            return <span style={{ color: DEBUG_JSON_COLORS.boolean }}>{String(value)}</span>;
        default:
            return <span style={{ color: DEBUG_JSON_COLORS.text }}>{String(value)}</span>;
    }
}

function JsonNode({
    name,
    value,
    depth = 0,
    path = "",
    filterText = "",
    onCopyPath,
    onCopyValue,
}: {
    name?: string;
    value: unknown;
    depth?: number;
    path?: string;
    filterText?: string;
    onCopyPath: (path: string) => void;
    onCopyValue: (value: unknown) => void;
}): JSX.Element | null {
    if (!nodeMatchesFilter(value, filterText, path)) {
        return null;
    }

    const keyPrefix = name ? (
        <>
            <span style={{ color: DEBUG_JSON_COLORS.key }}>&quot;{name}&quot;</span>
            <span>: </span>
        </>
    ) : null;

    if (value === null || typeof value !== "object") {
        return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1 }}>
                    {keyPrefix}
                    {renderJsonPrimitive(value)}
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCopyPath(formatNodePath(path));
                    }}
                >
                    Copy Path
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCopyValue(value);
                    }}
                >
                    Copy Value
                </button>
            </div>
        );
    }

    if (Array.isArray(value)) {
        const openByDefault = depth < 2;
        return (
            <details open={openByDefault}>
                <summary style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ flex: 1 }}>
                        {keyPrefix}[{value.length}]
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onCopyPath(formatNodePath(path));
                        }}
                    >
                        Copy Path
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onCopyValue(value);
                        }}
                    >
                        Copy Value
                    </button>
                </summary>
                <div style={{ marginLeft: 16 }}>
                    {value.map((item, index) => (
                        <JsonNode
                            key={`${name ?? "root"}-arr-${index}`}
                            name={String(index)}
                            value={item}
                            depth={depth + 1}
                            path={`${path}[${index}]`}
                            filterText={filterText}
                            onCopyPath={onCopyPath}
                            onCopyValue={onCopyValue}
                        />
                    ))}
                </div>
            </details>
        );
    }

    const entries = Object.entries(value as Record<string, unknown>);
    const openByDefault = depth < 2;

    return (
        <details open={openByDefault}>
            <summary style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ flex: 1 }}>
                    {keyPrefix}
                    {"{"} {entries.length} key{entries.length === 1 ? "" : "s"} {"}"}
                </span>
                <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCopyPath(formatNodePath(path));
                    }}
                >
                    Copy Path
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCopyValue(value);
                    }}
                >
                    Copy Value
                </button>
            </summary>
            <div style={{ marginLeft: 16 }}>
                {entries.map(([entryKey, entryValue]) => (
                    <JsonNode
                        key={`${name ?? "root"}-obj-${entryKey}`}
                        name={entryKey}
                        value={entryValue}
                        depth={depth + 1}
                        path={path ? `${path}.${entryKey}` : entryKey}
                        filterText={filterText}
                        onCopyPath={onCopyPath}
                        onCopyValue={onCopyValue}
                    />
                ))}
            </div>
        </details>
    );
}

export function JSONDebugPrint({ value }: JSONDebugPrintProps): JSX.Element {
    const [debugJsonFilter, setDebugJsonFilter] = useState("");
    const [debugCopyStatus, setDebugCopyStatus] = useState("");
    const debugJsonTreeRef = useRef<HTMLDivElement | null>(null);

    const setAllJsonNodesOpen = (open: boolean) => {
        if (!debugJsonTreeRef.current) {
            return;
        }

        const allNodes = debugJsonTreeRef.current.querySelectorAll("details");
        allNodes.forEach((node) => {
            node.open = open;
        });
    };

    const copyDebugText = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setDebugCopyStatus(`${label} copied`);
        } catch {
            setDebugCopyStatus(`Failed to copy ${label.toLowerCase()}`);
        }

        window.setTimeout(() => setDebugCopyStatus(""), 1200);
    };

    return (
        <>
            <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setAllJsonNodesOpen(true)}>
                    Expand All
                </button>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setAllJsonNodesOpen(false)}>
                    Collapse All
                </button>
                <input
                    type="text"
                    value={debugJsonFilter}
                    onChange={(e) => setDebugJsonFilter(e.target.value)}
                    placeholder="Filter by key, path or value"
                    style={{
                        minWidth: 240,
                        flex: 1,
                        fontFamily: "'Consolas', 'Courier New', monospace",
                        fontSize: "13px",
                    }}
                />
                <span style={{ color: DEBUG_JSON_COLORS.text, fontSize: "12px" }}>{debugCopyStatus}</span>
            </div>
            <div
                ref={debugJsonTreeRef}
                style={{
                    fontSize: "13px",
                    maxHeight: 500,
                    overflowY: "auto",
                    background: DEBUG_JSON_COLORS.bg,
                    color: DEBUG_JSON_COLORS.text,
                    padding: "0.75rem",
                    borderRadius: 4,
                    fontFamily: "'Consolas', 'Courier New', monospace",
                    lineHeight: 1.5,
                }}
            >
                <JsonNode
                    value={value}
                    filterText={debugJsonFilter}
                    onCopyPath={(path) => copyDebugText(path, "Path")}
                    onCopyValue={(nodeValue) => copyDebugText(formatNodeValue(nodeValue), "Value")}
                />
            </div>
        </>
    );
}
